import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import moment from 'moment/moment';

import { getProductPromotionsState } from '../../../../../features/price/get_product_promotions';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../core/constants';
import { DeleteButton } from '../../../../common/button';
import { deleteProductPromotionAction } from '../../../../../features/price/delete_product_promotion';
import { deletePromotionNotificationData } from '../../../../../core/utils/notification_utils';
import useNotification from '../../../../../core/hooks/use_notification';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { getAllPodsState } from '../../../../../features/pod/get_all_pods';
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material';

const ProductPromotionsList = ({ refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const getProductPromotions = useSelector(getProductPromotionsState);
    const getAllPods = useSelector(getAllPodsState);

    const [deletingIds, setDeletingIds] = useState({});

    const onDeleteProductPromotion = (productPromotionId) => {
        setDeletingIds(prev => ({ ...prev, [productPromotionId]: true }));

        dispatch(deleteProductPromotionAction(productPromotionId))
            .then(response => {
                showNotification(deletePromotionNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            })
            .finally(() => {
                setDeletingIds(prev => ({ ...prev, [productPromotionId]: false }));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Punkt sprzedaży</TableCell>
                        <TableCell align="center">Akcja promocyjna</TableCell>
                        <TableCell align="center">Cena bazowa</TableCell>
                        <TableCell align="center">Cena promocyjna</TableCell>
                        <TableCell align="center">Okres obowiązywania</TableCell>
                        <TableCell align="center">Łączy się z prom. na krótką datę?</TableCell>
                        <TableCell align="right" sx={ { pr: 3 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getProductPromotions?.data?.content?.map((productPromotion, index) => {
                            const podName = getAllPods?.data?.find(pod => pod?.id === productPromotion?.podId);
                            const podLabel = podName ? `${ podName?.indoorPartner?.name } (${ podName?.description }) - ${ podName?.device?.serviceId || 'brak urządzenia' }` : 'Brak danych';
                            const isDeleting = deletingIds[productPromotion?.productPromotionId] || false;

                            return (
                                <TableRow key={ index } hover>
                                    <TableCell sx={ { fontWeight: 500 } }>
                                        { podLabel }
                                    </TableCell>
                                    <TableCell align="center">
                                        {
                                            productPromotion?.isPromotionCampaign
                                                ? <CheckIcon color="success" fontSize="small"/>
                                                : <ClearIcon color="error" fontSize="small"/>
                                        }
                                    </TableCell>
                                    <TableCell
                                        align="center">{ productPromotion?.basicGrossPrice?.toFixed(2) } zł</TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" sx={ { fontWeight: 600, color: 'success.main' } }>
                                            { productPromotion?.promotionGrossPrice?.toFixed(2) } zł
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
                                            <Typography variant="body2">
                                                Od:&nbsp;{ moment(productPromotion?.timeFrom, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Do:&nbsp;{ moment(productPromotion?.timeTo, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        {
                                            !productPromotion?.excludeExpirationPromotion
                                                ? <CheckIcon color="success" fontSize="small"/>
                                                : <ClearIcon color="error" fontSize="small"/>
                                        }
                                    </TableCell>
                                    <TableCell align="right" sx={ { pr: 2 } }>
                                        <Stack direction="row" spacing={ 1 } justifyContent="flex-end"
                                               alignItems="center">
                                            {
                                                !productPromotion?.isPromotionCampaign &&
                                                <DeleteButton
                                                    size="small"
                                                    onDeleteClick={ () => onDeleteProductPromotion(productPromotion?.productPromotionId) }
                                                    isLoading={ isDeleting }
                                                />
                                            }
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProductPromotionsList;